import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/data';
import ArticleCard from '@/components/base/ArticleCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">分类未找到</h1>
          <p className="text-foreground-600 text-sm mb-4">请检查链接是否正确</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 水蜜桃视频`,
    description: category.description,
    url: `https://teadaye.cn/category/${slug}`,
    hasPart: categoryArticles.map((article) => ({
      '@type': 'Article',
      headline: article.title,
      url: `https://teadaye.cn/article/${article.id}`,
      author: { '@type': 'Person', name: article.author },
    })),
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 水蜜桃视频 | ${category.description.slice(0, 30)}`}
        description={`${category.description} 水蜜桃视频为您提供最专业的${category.name}相关短视频和图文内容。`}
        keywords={`水蜜桃视频,${category.name},水蜜桃,果园短视频`}
        canonicalUrl={`/category/${slug}`}
        jsonLd={categoryJsonLd}
      />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />
        </div>

        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src={category.coverUrl}
            alt={category.name}
            title={`${category.name} - 水蜜桃视频`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/45 flex items-end">
            <div className="w-full px-4 md:px-6 pb-8 md:pb-12">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-background-50/20 flex items-center justify-center">
                  <i className={`${category.icon} text-background-50 text-lg`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                  {category.name}
                </h1>
              </div>
              <p className="text-background-50/80 text-sm md:text-base max-w-xl">
                {category.description}
              </p>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-3 mb-8 overflow-x-auto pb-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">分类切换：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}